import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { font } from '../theme';
import {
  Table, Card, Button, Input, Select, Space, Tag, Modal, Form, Upload,
  message, Row, Col, Tooltip, Popconfirm, Typography, Divider, Descriptions, DatePicker,
} from 'antd';
import {
  SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  UploadOutlined, FileExcelOutlined, ExperimentOutlined, CloseOutlined,
  FilterOutlined, CloseCircleOutlined, EyeOutlined, CalendarOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { testMaterialAPI } from '../services/api';
import { usePermission } from '../contexts/PermissionContext';
import { brand, gray, radius } from '../theme/tokens';
import { EmptyState } from '../components/common';
import { useFitTableHeight } from '../hooks/useViewportFit';

const { Option } = Select;

interface TestMaterial {
  id: number;
  schedule_id: number | null;
  schedule_title: string;
  test_number: string;
  sample_no: string;
  title: string;
  pesticide_name: string;
  active_ingredient: string;
  lot_no: string;
  received_qty: string;
  manufactured_date: string;
  guarantee_date: string;
  company_id: number | null;
  company_name: string;
  manufacturer: string;
  received_date: string;
  disposal_date: string;
  crop_disposal: string;
  disposal_confirm: string;
  sort_no: number | null;
  note: string;
}

interface MaterialSummary {
  total: number;
  disposed: number;
  not_disposed: number;
  soon_days: number;
  by_storage: { code: string; name: string; count: number }[];
  gaps: { received_missing: number; lot_missing: number; qty_missing: number; schedule_unlinked: number };
}

/* 작물폐기유무 — 원본 대장 사용값.
   색은 검증된 카테고리 팔레트에서 인접 순서까지 맞춰 고른 값이다 (2026-08 교체).
   이전 팔레트는 3-1(#8b5cf6)↔3-2(#6366f1) 가 정상 시야에서도 ΔE 6.3(기준 15)이라
   색으로는 사실상 구분이 안 됐다. 현재 순서 기준 색약 인접 분리 ΔE 9.1 / 정상 19.6.
   ※ 이 순서(CROP_DISPOSAL_CODES)가 곧 스택바 세그먼트 순서다 — 순서를 바꾸면 다시 검증할 것 */
const CROP_DISPOSAL_COLORS: Record<string, string> = {
  '1': '#2a78d6', '2': '#eb6834', '3-1': '#1baf7a', '3-2': '#eda100',
  '3-3': '#e87ba4', '기초시험': '#008300', '유기농': '#4a3aa7',
};
/* 미지정(값 없음)은 의도적으로 무채색 — 채도 하한 미달은 이 슬롯에 한해 정상이다 */
const CROP_DISPOSAL_UNSET_COLOR = '#94a3b8';

/* 상단 요약 축 = 시료의 보관·기한 상태 (2026-08, 작물폐기유무 분포에서 교체).
   대장의 목적이 인수→보관→폐기 추적이라 이 축이 곧 담당자의 할 일이 된다.
   색은 심각도 순서 그대로가 스택바 세그먼트 순서다 — 순서를 바꾸면 인접 대비를 다시 검증할 것.
   현재 인접 분리 ΔE: 정상 최소 30.7(기준 15) / 색약(1·2형) 최소 19.4(기준 8). */
const STORAGE_ORDER = ['expired', 'soon', 'ok', 'unknown', 'disposed'] as const;
const STORAGE_COLORS: Record<string, string> = {
  expired: '#b91c1c',   // 기한 경과 — 사용 금지 대상
  soon: '#f59e0b',      // 기한 임박
  ok: '#15803d',        // 기한 여유
  unknown: '#94a3b8',   // 기한 미기재 (무채색 의도)
  disposed: '#475569',  // 폐기 완료
};
const STORAGE_LABEL = (code: string) =>
  ({ expired: '기한 경과', soon: '기한 임박', ok: '기한 여유',
     unknown: '기한 미기재', disposed: '폐기 완료' } as Record<string, string>)[code] || code;
const STORAGE_HINTS: Record<string, string> = {
  expired: '보증기한이 지난 시료 — 시험 사용 전 확인이 필요합니다',
  soon: '보증기한이 얼마 남지 않은 시료',
  ok: '보증기한 여유가 있는 보관 시료',
  unknown: '보증기한 칸이 비어 있어 사용 가부를 판단할 수 없는 시료',
  disposed: '폐기일이 기록된 시료',
};

const CropDisposalBadge: React.FC<{ value?: string }> = ({ value }) => {
  if (!value) return <Typography.Text type="secondary">-</Typography.Text>;
  const c = CROP_DISPOSAL_COLORS[value] || CROP_DISPOSAL_UNSET_COLOR;
  return (
    <Tag style={{ fontWeight: 600, color: c, borderColor: c + '55', background: c + '14', marginRight: 0 }}>
      {value}
    </Tag>
  );
};

/* 시험년도 표기 — 대장은 시험번호에 2자리로만 들어 있다(SR26-…). 화면에는 네 자리로 보여준다 */
const yearLabel = (y: string) => (/^\d{2}$/.test(y) ? `20${y}` : y);

/* 섹션 구분바 — 시험일정 수정 폼과 동일한 패턴 */
const SectionBar: React.FC<{ label: string; color: string; bg: string }> = ({ label, color, bg }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px',
    background: bg, borderLeft: `3px solid ${color}`, borderRadius: 4, margin: '4px 0 12px',
  }}>
    <span style={{ fontWeight: 600, fontSize: 11, color }}>{label}</span>
  </div>
);

const TestMaterialPage: React.FC = () => {
  const [items, setItems] = useState<TestMaterial[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterManufacturer, setFilterManufacturer] = useState('');
  const [filterCropDisposal, setFilterCropDisposal] = useState('');
  /* 시험설계서 목록과 같은 축(시험년도·시험작물·대상병해충) + 대장 고유 축(약제명).
     앞의 셋은 대장에 컬럼이 없어 시험번호·항목명에서 파생시킨 값이라 서버가 판정한다
     (목록이 서버 페이징이라 화면에서 거르면 페이지에 있는 행만 걸러진다) */
  const [filterYear, setFilterYear] = useState('');
  const [filterCrop, setFilterCrop] = useState('');
  const [filterDisease, setFilterDisease] = useState('');
  const [filterPesticide, setFilterPesticide] = useState('');
  /* 상단 카드가 축을 소유한다 — 기존 '폐기여부' 드롭다운은 이 축의 일부라 제거했다 */
  const [filterStorage, setFilterStorage] = useState('');
  const [page, setPage] = useState(1);
  /* 기본 50 → 20 (2026-08). 이 화면은 열이 18개라 50행이면 셀 918개로 전 화면 중 최다이고,
     실측상 첫 행 표시 1,451ms 의 대부분이 그 렌더 비용이었다. 대장 훑기는 건수 셀렉터로 올려 쓴다 */
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<{
    companies: string[]; manufacturers: string[]; crop_disposals: string[];
    years: string[]; crops: string[]; diseases: string[]; pesticides: string[];
  }>({
    companies: [], manufacturers: [], crop_disposals: [],
    years: [], crops: [], diseases: [], pesticides: [],
  });
  const [summary, setSummary] = useState<MaterialSummary | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TestMaterial | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const [detail, setDetail] = useState<TestMaterial | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [uploading, setUploading] = useState(false);
  // 선택 n건 일자 일괄등록 (인수일 / 폐기일) — 시험일정총괄표와 동일 패턴
  const [bulkDateOpen, setBulkDateOpen] = useState(false);
  const [bulkDateSaving, setBulkDateSaving] = useState(false);
  const [bulkDateForm] = Form.useForm();

  const { hasPermission } = usePermission();
  const canWrite = hasPermission('test_materials', 'WRITE');
  const canDelete = hasPermission('test_materials', 'DELETE');
  /* 체크박스는 일자 일괄등록(WRITE) 전용 — 선택삭제는 폐기(2026-08). 삭제는 행 단위로만 한다 */
  const canSelect = canWrite;

  /* 표 본문 높이 실측 — 공용 훅 (매직넘버 없이 페이지 세로스크롤 방지) */
  const { tableWrapRef, cardWrapRef, bodyHeight } = useFitTableHeight();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await testMaterialAPI.list({
        search: search.trim() || undefined,
        company_name: filterCompany || undefined,
        manufacturer: filterManufacturer || undefined,
        crop_disposal: filterCropDisposal || undefined,
        storage_status: filterStorage || undefined,
        test_year: filterYear || undefined,
        crop: filterCrop || undefined,
        target_disease: filterDisease || undefined,
        pesticide_name: filterPesticide || undefined,
        page, page_size: pageSize,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      message.error('시험물질 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [search, filterCompany, filterManufacturer, filterCropDisposal, filterStorage,
      filterYear, filterCrop, filterDisease, filterPesticide, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* 검색 결과 요약 카드 — 목록이 서버 페이징이라 화면에서 셀 수 없어 서버 집계를 쓴다.
     보관·기한 상태는 카드 축이므로 파라미터에서 빼고 조회한다(그래야 하나를 골라도 분포가 유지됨). */
  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await testMaterialAPI.summary({
        search: search.trim() || undefined,
        company_name: filterCompany || undefined,
        manufacturer: filterManufacturer || undefined,
        crop_disposal: filterCropDisposal || undefined,
        test_year: filterYear || undefined,
        crop: filterCrop || undefined,
        target_disease: filterDisease || undefined,
        pesticide_name: filterPesticide || undefined,
      });
      setSummary(data);
    } catch { setSummary(null); }
  }, [search, filterCompany, filterManufacturer, filterCropDisposal,
      filterYear, filterCrop, filterDisease, filterPesticide]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  /* 등록·수정·삭제·업로드처럼 데이터가 바뀐 뒤에는 목록과 요약 카드를 함께 다시 읽는다 */
  const reload = useCallback(() => { fetchData(); fetchSummary(); }, [fetchData, fetchSummary]);

  useEffect(() => {
    testMaterialAPI.filters().then(({ data }) => setFilters(data)).catch(() => {});
  }, []);

  // 목록이 바뀌면(페이지·필터 이동 포함) 보이지 않는 행의 선택은 해제
  useEffect(() => {
    setSelectedIds(prev => prev.filter(id => items.some(m => m.id === id)));
  }, [items]);

  const activeFilterCount = useMemo(
    () => [filterYear, filterCrop, filterDisease, filterPesticide,
           filterCompany, filterManufacturer, filterCropDisposal, filterStorage].filter(Boolean).length,
    [filterYear, filterCrop, filterDisease, filterPesticide,
     filterCompany, filterManufacturer, filterCropDisposal, filterStorage],
  );

  /* 보관·기한 상태 카드 — 값이 0이어도 항상 노출한다(사라지면 토글 해제할 카드가 없어진다).
     0건인 세그먼트도 그 자체가 정보다: '폐기 완료 0건'은 폐기 기록이 아직 없다는 뜻이다 */
  const storageCards = useMemo(() => {
    const rows = summary?.by_storage
      || STORAGE_ORDER.map(c => ({ code: c, name: STORAGE_LABEL(c), count: 0 }));
    const base = summary?.total || 0;
    return rows.map(r => ({
      code: r.code,
      name: r.name,
      count: r.count,
      pct: base ? Math.round((r.count / base) * 100) : 0,
      color: STORAGE_COLORS[r.code] || CROP_DISPOSAL_UNSET_COLOR,
    }));
  }, [summary]);

  /* 대장 기재 점검 — 감사 대비로 비어 있으면 안 되는 칸. 0건이면 아예 노출하지 않는다 */
  const gapChips = useMemo(() => {
    const g = summary?.gaps;
    if (!g) return [];
    return [
      { key: 'received', label: '인수일 미기재', count: g.received_missing },
      { key: 'lot', label: 'Lot 미기재', count: g.lot_missing },
      { key: 'qty', label: '인수량 미기재', count: g.qty_missing },
      { key: 'sched', label: '시험일정 미연결', count: g.schedule_unlinked },
    ].filter(c => c.count > 0);
  }, [summary]);

  /* 현재 걸려 있는 검색·필터를 사람이 읽는 문장으로 (요약 카드 부제) */
  const activeFilterSummary = useMemo(() => {
    const parts: string[] = [];
    if (filterYear) parts.push(`${yearLabel(filterYear)}년`);
    if (filterCrop) parts.push(filterCrop);
    if (filterDisease) parts.push(filterDisease);
    if (filterPesticide) parts.push(filterPesticide);
    if (filterCompany) parts.push(filterCompany);
    if (filterManufacturer) parts.push(filterManufacturer);
    if (filterCropDisposal) parts.push(`폐기유무 ${filterCropDisposal}`);
    if (filterStorage) parts.push(STORAGE_LABEL(filterStorage));
    if (search.trim()) parts.push(`"${search.trim()}"`);
    return parts.length ? parts.join(' · ') : '전체';
  }, [filterYear, filterCrop, filterDisease, filterPesticide,
      filterCompany, filterManufacturer, filterCropDisposal, filterStorage, search]);

  const resetFilters = () => {
    setFilterYear(''); setFilterCrop(''); setFilterDisease(''); setFilterPesticide('');
    setFilterCompany(''); setFilterManufacturer('');
    setFilterCropDisposal(''); setFilterStorage(''); setPage(1);
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setFormOpen(true);
  };

  const openEdit = (record: TestMaterial) => {
    setEditing(record);
    form.setFieldsValue(record);
    setFormOpen(true);
  };

  const submitForm = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await testMaterialAPI.update(editing.id, values);
        message.success('수정되었습니다.');
      } else {
        await testMaterialAPI.create(values);
        message.success('등록되었습니다.');
      }
      setFormOpen(false);
      reload();
    } catch (err: any) {
      message.error(err.response?.data?.detail || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await testMaterialAPI.delete(id);
      message.success('삭제되었습니다.');
      reload();
    } catch (err: any) {
      message.error(err.response?.data?.detail || '삭제에 실패했습니다.');
    }
  };

  const submitBulkDates = async () => {
    const v = await bulkDateForm.validateFields();
    const payload: { ids: number[]; received_date?: string; disposal_date?: string } = { ids: selectedIds };
    if (v.received_date) payload.received_date = dayjs(v.received_date).format('YYYY-MM-DD');
    if (v.disposal_date) payload.disposal_date = dayjs(v.disposal_date).format('YYYY-MM-DD');
    if (!payload.received_date && !payload.disposal_date) {
      message.warning('인수일 또는 폐기일 중 하나 이상을 입력하세요.');
      return;
    }
    setBulkDateSaving(true);
    try {
      const { data } = await testMaterialAPI.bulkDates(payload);
      message.success(data?.message || `${selectedIds.length}건에 반영되었습니다.`);
      setBulkDateOpen(false);
      bulkDateForm.resetFields();
      setSelectedIds([]);
      reload();
    } catch (err: any) {
      message.error(err.response?.data?.detail || '일자 일괄등록에 실패했습니다.');
    } finally {
      setBulkDateSaving(false);
    }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const { data } = await testMaterialAPI.uploadExcel(file);
      message.success(data.message, 5);
      if (data.unmatched_count > 0) {
        Modal.info({
          title: '시험일정과 매칭되지 않은 시험번호',
          width: 560,
          content: (
            <div>
              <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
                아래 시험번호는 시험일정에 없어 시험 연결 없이 대장에만 등록되었습니다.
                시험일정이 등록되면 해당 물질을 다시 저장할 때 자동으로 연결됩니다.
              </Typography.Paragraph>
              <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                {(data.unmatched_test_numbers || []).map((t: string) => (
                  <Tag key={t} style={{ marginBottom: 4 }}>{t}</Tag>
                ))}
              </div>
              {data.unmatched_count > (data.unmatched_test_numbers || []).length && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  … 외 {data.unmatched_count - (data.unmatched_test_numbers || []).length}종
                </Typography.Text>
              )}
            </div>
          ),
        });
      }
      reload();
    } catch (err: any) {
      message.error(err.response?.data?.detail || '업로드에 실패했습니다.');
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleExport = async () => {
    try {
      const { data } = await testMaterialAPI.exportExcel();
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `시험물질관리대장_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      message.success('엑셀이 다운로드되었습니다.');
    } catch {
      message.error('다운로드에 실패했습니다.');
    }
  };

  const columns = [
    { title: '연번', dataIndex: 'sort_no', width: 58, fixed: 'left' as const,
      render: (v: number | null) => <Typography.Text type="secondary" style={{ fontSize: 12 }}>{v ?? '-'}</Typography.Text> },
    { title: '시험번호', dataIndex: 'test_number', width: 130, fixed: 'left' as const,
      render: (v: string, r: TestMaterial) => (
        <Tooltip title={r.schedule_id ? '시험일정과 연결됨' : '시험일정 미등록 (대장 단독 보관)'}>
          <Typography.Text strong style={{ color: r.schedule_id ? brand.primary : gray[500], fontSize: 12 }}>
            {v}{!r.schedule_id && ' *'}
          </Typography.Text>
        </Tooltip>
      ) },
    { title: '시료번호', dataIndex: 'sample_no', width: 130,
      render: (v: string) => <Tag color="blue" style={{ fontFamily: font.mono, fontWeight: 600, marginRight: 0 }}>{v}</Tag> },
    { title: '항목명', dataIndex: 'title', width: 250, ellipsis: true,
      render: (v: string, r: TestMaterial) => (
        <Tooltip title={v}><a onClick={() => setDetail(r)}>{v || '-'}</a></Tooltip>
      ) },
    { title: '약제명', dataIndex: 'pesticide_name', width: 220, ellipsis: true },
    { title: '주성분 함량(%)', dataIndex: 'active_ingredient', width: 150, ellipsis: true },
    { title: 'Lot.', dataIndex: 'lot_no', width: 160, ellipsis: true },
    { title: '인수량', dataIndex: 'received_qty', width: 88, align: 'center' as const },
    { title: '제조년월일', dataIndex: 'manufactured_date', width: 105, align: 'center' as const,
      render: (v: string) => v || <Typography.Text type="secondary">-</Typography.Text> },
    { title: '약효보증기간', dataIndex: 'guarantee_date', width: 112, align: 'center' as const,
      render: (v: string) => v || <Typography.Text type="secondary">-</Typography.Text> },
    { title: '의뢰사', dataIndex: 'company_name', width: 96,
      render: (v: string) => v ? <Tag style={{ marginRight: 0 }}>{v}</Tag> : '-' },
    { title: '제조사', dataIndex: 'manufacturer', width: 96 },
    { title: '인수일', dataIndex: 'received_date', width: 100, align: 'center' as const,
      render: (v: string) => v || <Typography.Text type="secondary">-</Typography.Text> },
    { title: '폐기일', dataIndex: 'disposal_date', width: 100, align: 'center' as const,
      render: (v: string) => v || <Typography.Text type="secondary">-</Typography.Text> },
    { title: '작물폐기유무', dataIndex: 'crop_disposal', width: 104, align: 'center' as const,
      render: (v: string) => <CropDisposalBadge value={v} /> },
    { title: '폐기확인서', dataIndex: 'disposal_confirm', width: 96, align: 'center' as const,
      render: (v: string) => v ? <Tag color="green" style={{ marginRight: 0 }}>{v}</Tag> : <Typography.Text type="secondary">-</Typography.Text> },
    {
      title: '작업', key: 'actions', width: 92, fixed: 'right' as const, align: 'center' as const,
      render: (_: any, record: TestMaterial) => (
        <Space size={2}>
          <Tooltip title="상세">
            <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => setDetail(record)} />
          </Tooltip>
          {canWrite && (
            <Tooltip title="수정">
              <Button type="text" size="small" icon={<EditOutlined />}
                style={{ color: brand.primary }} onClick={() => openEdit(record)} />
            </Tooltip>
          )}
          {canDelete && (
            <Popconfirm title="삭제하시겠습니까?" okText="삭제" cancelText="취소"
              okButtonProps={{ danger: true }} onConfirm={() => handleDelete(record.id)}>
              <Tooltip title="삭제">
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      {/* ── 검색 결과 요약 (히어로 건수 + 보관·기한 상태 스택바 + 기재 점검) ──
           축은 작물폐기유무 분포에서 2026-08 교체했다. 작물폐기유무는 시험(작물) 속성이라
           시료 단위로 세면 같은 시험이 중복 계산되고, 분포를 봐도 담당자가 할 일이 안 나온다.
           대장이 답해야 하는 질문은 '지금 무엇을 보관 중이고 무엇이 위험한가'다.
           부분의 합 = 전체이므로 타일 나열 대신 가로 100% 스택바 하나로 묶고,
           색만으로 구분하지 않도록 범례에 상태명·건수·%를 항상 문자로 적는다. */}
      <div style={{
        background: '#fff', borderRadius: 8, padding: '10px 12px',
        border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        position: 'relative', marginBottom: 8,
      }}>
            {/* 머리줄 — 건수·조건·축 이름·상태필터를 한 줄에. 시험일정총괄표의 요약 카드와 같은 형태다
                (2026-09-03 밀도 키트: 히어로 숫자 40px 이 왼쪽 열을 통째로 차지하고
                 범례가 남은 폭에서 접히던 구조를 전폭 스택으로 바꿨다) */}
            <div style={{
              display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
              paddingRight: 18,   /* 우상단 도움말 아이콘 자리 */
            }}>
              <span style={{ fontSize: 11, color: gray[400], fontWeight: 600, letterSpacing: '0.04em' }}>
                검색 결과
              </span>
              <span style={{ fontSize: 20, fontWeight: 700, color: gray[800], lineHeight: 1.15 }}>
                {summary?.total ?? total}
              </span>
              <span style={{ fontSize: 12, fontWeight: 500, color: gray[400], marginLeft: -4 }}>건</span>
              <span style={{ color: '#e2e8f0' }}>·</span>
              <span style={{ fontSize: 11, color: gray[400], flex: 1, minWidth: 80,
                             whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    title={activeFilterSummary}>
                {activeFilterSummary}
              </span>
              <span style={{ fontSize: 11, color: gray[400], fontWeight: 600 }}>
                보관·기한 상태 <span style={{ fontWeight: 500 }}>
                (기한 임박 = {summary?.soon_days ?? 180}일 이내)</span>
              </span>
              {filterStorage && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 500 }}>
                  <span style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: STORAGE_COLORS[filterStorage] || CROP_DISPOSAL_UNSET_COLOR,
                  }} />
                  <span style={{ color: gray[600] }}>{STORAGE_LABEL(filterStorage)} {total}건만 표시 중</span>
                  <span role="button" tabIndex={0}
                    onClick={() => { setFilterStorage(''); setPage(1); }}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFilterStorage(''); setPage(1); } }}
                    style={{ cursor: 'pointer', color: gray[400], marginLeft: 2 }}>✕</span>
                </span>
              )}
            </div>

            {/* 스택바 — 세그먼트 사이 2px 표면 간격, 양끝만 라운드 */}
            <div style={{
              display: 'flex', gap: 2, height: 14, borderRadius: 4, marginTop: 8,
              background: '#f1f5f9', overflow: 'hidden',
            }}>
              {storageCards.filter(c => c.count > 0).map(c => {
                const dim = !!filterStorage && filterStorage !== c.code;
                return (
                  <Tooltip key={c.code} title={`${c.name} ${c.count}건 (${c.pct}%) — ${STORAGE_HINTS[c.code] || ''}`}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => { setFilterStorage(filterStorage === c.code ? '' : c.code); setPage(1); }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setFilterStorage(filterStorage === c.code ? '' : c.code); setPage(1);
                        }
                      }}
                      style={{
                        flex: `${c.count} 0 0`, minWidth: 3, background: c.color,
                        opacity: dim ? 0.25 : 1, cursor: 'pointer', transition: 'opacity .15s',
                      }}
                    />
                  </Tooltip>
                );
              })}
            </div>

            {/* 범례 — 텍스트는 잉크색, 정체성은 옆의 색 점이 담당 */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 8 }}>
              {storageCards.map(c => {
                const active = filterStorage === c.code;
                const zero = c.count === 0;
                return (
                  <span
                    key={c.code}
                    role="button"
                    tabIndex={0}
                    onClick={() => { setFilterStorage(active ? '' : c.code); setPage(1); }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault(); setFilterStorage(active ? '' : c.code); setPage(1);
                      }
                    }}
                    title={`${c.name} · ${c.count}건 (${c.pct}%) — ${STORAGE_HINTS[c.code] || ''}. 클릭하면 이 상태만 조회합니다`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '2px 8px 2px 6px', borderRadius: 20, cursor: 'pointer',
                      background: active ? '#f1f5f9' : 'transparent',
                      boxShadow: active ? `inset 0 0 0 1px ${c.color}` : 'none',
                      opacity: zero && !active ? 0.45 : 1, transition: 'all .15s',
                    }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: gray[600], fontWeight: active ? 700 : 500 }}>{c.name}</span>
                    <span style={{ fontSize: 12, color: gray[800], fontWeight: 700 }}>{c.count}</span>
                    <span style={{ fontSize: 11, color: gray[400] }}>({c.pct}%)</span>
                  </span>
                );
              })}
            </div>

            {/* 대장 기재 점검 — 감사 때 비어 있으면 안 되는 칸. 0건이면 줄 자체가 사라진다 */}
            {gapChips.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                            gap: '4px 10px', marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e2e8f0' }}>
                <span style={{ fontSize: 11, color: gray[400], fontWeight: 600 }}>기재 점검</span>
                {gapChips.map(g => (
                  <span key={g.key}
                    title={`${g.label} ${g.count}건 — 대장에 비어 있는 칸입니다`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '1px 8px', borderRadius: 20, background: '#fff7ed',
                      border: '1px solid #fed7aa', fontSize: 11, color: '#9a3412',
                    }}>
                    {g.label}
                    <b style={{ fontWeight: 700 }}>{g.count}</b>
                  </span>
                ))}
              </div>
            )}

        <Tooltip
          title={`현재 검색·필터 조건에 걸린 시료 건수와 보관·기한 상태 분포입니다. 조건: ${activeFilterSummary}. 막대와 범례를 클릭하면 해당 상태만 조회하고, 다시 누르면 해제됩니다. 기한은 보증기한(guarantee) 기준이며, 폐기일이 기록되면 '폐기 완료'로 집계됩니다.`}
          overlayStyle={{ maxWidth: 300 }} placement="topRight">
          <QuestionCircleOutlined style={{
            position: 'absolute', top: 8, right: 10,
            fontSize: 11, color: gray[300], cursor: 'pointer',
          }} />
        </Tooltip>
      </div>

      <div ref={cardWrapRef}>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ExperimentOutlined style={{ color: brand.primary, fontSize: 18 }} />
            <div>
              <Typography.Title level={5} style={{ margin: 0 }}>시험물질관리</Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                총 {total}건{activeFilterCount > 0 && ' (필터 적용)'}
              </Typography.Text>
            </div>
          </div>
        }
        extra={
          <Space>
            <Input
              placeholder="시험번호, 시료번호, 항목명, 약제명, Lot 검색"
              prefix={<SearchOutlined style={{ color: gray[400] }} />}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              allowClear
              style={{ width: 290, borderRadius: radius.md }}
            />
            <Tooltip title="새로고침">
              <Button icon={<ReloadOutlined />} onClick={reload} style={{ borderRadius: radius.md }} />
            </Tooltip>
            <Button icon={<FileExcelOutlined />} onClick={handleExport} style={{ borderRadius: radius.md }}>
              Excel 다운로드
            </Button>
            {canWrite && (
              <Upload accept=".xlsx,.xlsm" showUploadList={false} beforeUpload={handleUpload}>
                <Button icon={<UploadOutlined />} loading={uploading} style={{ borderRadius: radius.md }}>
                  Excel 업로드
                </Button>
              </Upload>
            )}
            {canWrite && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}
                style={{ borderRadius: radius.md, backgroundColor: brand.primary, borderColor: brand.primary }}>
                등록
              </Button>
            )}
          </Space>
        }
      >
        {/* ── 필터바 ── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          padding: '8px 12px', marginBottom: 12, background: gray[50],
          border: `1px solid ${gray[200]}`, borderRadius: 8,
        }}>
          <FilterOutlined style={{ color: gray[400] }} />
          <Typography.Text style={{ fontSize: 12, color: gray[500], fontWeight: 600 }}>필터</Typography.Text>
          {/* 시험 축 — 시험설계서 목록(시험년도·대상병해충·시험작물)과 같은 축.
              대장엔 해당 컬럼이 없어 시험번호·항목명에서 파생시킨 값을 서버가 판정한다 */}
          <Select placeholder="📅 시험년도" allowClear showSearch size="small" style={{ width: 120 }}
            value={filterYear || undefined}
            onChange={(v) => { setFilterYear(v || ''); setPage(1); }}
            options={filters.years.map(y => ({ label: yearLabel(y), value: y }))} />
          <Select placeholder="🌱 시험작물" allowClear showSearch size="small" style={{ width: 140 }}
            value={filterCrop || undefined}
            onChange={(v) => { setFilterCrop(v || ''); setPage(1); }}
            options={filters.crops.map(c => ({ label: c, value: c }))} />
          <Select placeholder="🐛 대상병해충" allowClear showSearch size="small" style={{ width: 168 }}
            value={filterDisease || undefined}
            onChange={(v) => { setFilterDisease(v || ''); setPage(1); }}
            options={filters.diseases.map(c => ({ label: c, value: c }))} />
          <Select placeholder="🏢 의뢰사" allowClear showSearch size="small" style={{ width: 140 }}
            value={filterCompany || undefined}
            onChange={(v) => { setFilterCompany(v || ''); setPage(1); }}
            options={filters.companies.map(c => ({ label: c, value: c }))} />
          <Select placeholder="🏭 제조사" allowClear showSearch size="small" style={{ width: 140 }}
            value={filterManufacturer || undefined}
            onChange={(v) => { setFilterManufacturer(v || ''); setPage(1); }}
            options={filters.manufacturers.map(c => ({ label: c, value: c }))} />
          {/* 약제명은 184종이라 목록을 훑는 용도가 아니다 — 검색창은 부분일치, 이 칸은 정확일치 선택 */}
          <Select placeholder="💊 약제명" allowClear showSearch size="small" style={{ width: 180 }}
            value={filterPesticide || undefined}
            onChange={(v) => { setFilterPesticide(v || ''); setPage(1); }}
            options={filters.pesticides.map(c => ({ label: c, value: c }))} />
          <Select placeholder="♻ 작물폐기유무" allowClear size="small" style={{ width: 150 }}
            value={filterCropDisposal || undefined}
            onChange={(v) => { setFilterCropDisposal(v || ''); setPage(1); }}>
            {filters.crop_disposals.map(c => (
              <Option key={c} value={c}><CropDisposalBadge value={c} /></Option>
            ))}
          </Select>
          {activeFilterCount > 0 && (
            <Button size="small" type="text" danger icon={<CloseCircleOutlined />} onClick={resetFilters}>
              필터 초기화 ({activeFilterCount})
            </Button>
          )}
          {/* 시험번호 색 표식 범례 — 열을 설명하는 범례라 표 위가 제자리다.
              표 아래에 두면 페이지네이션 좌측(조회 건수)과 자리가 겹친다 */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              * 표시된 시험번호는 시험일정에 등록되지 않은 건입니다.
            </Typography.Text>
            <div style={{ fontSize: 12, color: gray[400] }}>{total}건 조회됨</div>
          </div>
        </div>

        <div ref={tableWrapRef} style={{ position: 'relative' }}>
        <Table
          dataSource={items}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          showSorterTooltip={false}
          scroll={{ x: 2100, y: bodyHeight }}
          sticky
          rowClassName={(_, index) => index % 2 === 0 ? '' : 'table-row-alt'}
          locale={{ emptyText: <EmptyState title="등록된 시험물질이 없습니다" size="sm" /> }}
          rowSelection={canSelect ? {
            selectedRowKeys: selectedIds,
            onChange: (keys) => setSelectedIds(keys as number[]),
            columnWidth: 40,
            fixed: true,
          } : undefined}
          pagination={{
            current: page, pageSize, total, showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}건`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
        />

        {/* 선택 n건 일괄작업 — 표 위에 떠 있는 바.
            흐름에 넣으면 체크할 때마다 표 높이가 밀려(bodyHeight 재계산까지 겹쳐) 화면이 들썩인다. */}
        {canSelect && selectedIds.length > 0 && (
          <div className="bulk-select-bar">
            <span className="bulk-select-count">{selectedIds.length}</span>
            <Typography.Text className="bulk-select-label">건 선택됨</Typography.Text>
            <Button type="primary" icon={<CalendarOutlined />}
              onClick={() => { bulkDateForm.resetFields(); setBulkDateOpen(true); }}
              className="bulk-select-btn">
              일자 일괄등록
            </Button>
            <Tooltip title="선택 해제">
              <Button type="text" icon={<CloseOutlined />} className="bulk-select-close"
                onClick={() => setSelectedIds([])} />
            </Tooltip>
          </div>
        )}
        </div>
      </Card>
      </div>

      {/* ── 등록 / 수정 ── */}
      <Modal
        title={
          <Space>
            {editing ? <EditOutlined style={{ color: brand.primary }} /> : <PlusOutlined style={{ color: brand.primary }} />}
            <span>{editing ? '시험물질 수정' : '시험물질 등록'}</span>
          </Space>
        }
        open={formOpen}
        onCancel={() => setFormOpen(false)}
        onOk={submitForm}
        okText="저장"
        cancelText="취소"
        confirmLoading={saving}
        width={900}
        destroyOnClose
        styles={{ body: { maxHeight: '72vh', overflowY: 'auto', paddingTop: 8 } }}
      >
        <Form form={form} layout="vertical" size="small">
          <SectionBar label="시험 · 시료 식별" color={brand.primary} bg="#eef2ff" />
          <Row gutter={10}>
            <Col span={4}>
              <Form.Item name="sort_no" label="연번" style={{ marginBottom: 10 }}>
                <Input placeholder="예: 1" />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item name="test_number" label="시험번호" rules={[{ required: true, message: '시험번호를 입력하세요' }]} style={{ marginBottom: 10 }}>
                <Input placeholder="예: SR26-FSA-01" />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item name="sample_no" label="시료번호" rules={[{ required: true, message: '시료번호를 입력하세요' }]} style={{ marginBottom: 10 }}>
                <Input placeholder="예: SR26-FSA-01A" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="crop_disposal" label="작물폐기유무" style={{ marginBottom: 10 }}>
                <Select allowClear placeholder="선택">
                  {(filters.crop_disposals.length ? filters.crop_disposals : ['1', '2', '3-1', '3-2', '3-3', '기초시험', '유기농']).map(c => (
                    <Option key={c} value={c}><CropDisposalBadge value={c} /></Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="title" label="항목명" style={{ marginBottom: 10 }}>
            <Input placeholder="비우면 시험번호로 매칭된 시험일정의 항목명이 채워집니다" />
          </Form.Item>

          <SectionBar label="물질 정보" color="#0891b2" bg="#ecfeff" />
          <Row gutter={10}>
            <Col span={12}>
              <Form.Item name="pesticide_name" label="약제명" style={{ marginBottom: 10 }}>
                <Input placeholder="예: 플룩사피록사드 액상수화제" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="active_ingredient" label="주성분 함량(%)"
                extra={<span style={{ fontSize: 10, color: gray[400] }}>복합 표기 가능 · 예: 3.65(0.65+3)</span>}
                style={{ marginBottom: 10 }}>
                <Input placeholder="예: 20" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="lot_no" label="Lot." style={{ marginBottom: 10 }}>
                <Input placeholder="예: 2601-0121" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={10}>
            <Col span={6}>
              <Form.Item name="received_qty" label="인수량"
                extra={<span style={{ fontSize: 10, color: gray[400] }}>단위 포함 · 예: 500g x2</span>}
                style={{ marginBottom: 10 }}>
                <Input placeholder="예: 100mL" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="manufactured_date" label="제조년월일"
                extra={<span style={{ fontSize: 10, color: gray[400] }}>'미표기' 등 텍스트 허용</span>}
                style={{ marginBottom: 10 }}>
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="guarantee_date" label="약효보증기간" style={{ marginBottom: 10 }}>
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="manufacturer" label="제조사" style={{ marginBottom: 10 }}>
                <Select allowClear showSearch placeholder="선택 또는 입력"
                  options={filters.manufacturers.map(c => ({ label: c, value: c }))} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="company_name" label="의뢰사"
            extra={<span style={{ fontSize: 10, color: gray[400] }}>의뢰사 마스터에 같은 이름이 있으면 자동 연결됩니다</span>}
            style={{ marginBottom: 10 }}>
            <Select allowClear showSearch placeholder="선택 또는 입력"
              options={filters.companies.map(c => ({ label: c, value: c }))} />
          </Form.Item>

          <SectionBar label="인수 · 폐기 현황" color="#059669" bg="#ecfdf5" />
          <Row gutter={10}>
            <Col span={8}>
              <Form.Item name="received_date" label="인수일" style={{ marginBottom: 10 }}>
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="disposal_date" label="폐기일" style={{ marginBottom: 10 }}>
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="disposal_confirm" label="폐기확인서 작성완료" style={{ marginBottom: 10 }}>
                <Input placeholder="예: 26년" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="note" label="비고" style={{ marginBottom: 0 }}>
            <Input.TextArea rows={2} placeholder="시료 라벨 불일치 등 특이사항" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── 상세 ── */}
      <Modal
        title={
          <Space>
            <ExperimentOutlined style={{ color: brand.primary }} />
            <span>시험물질 상세</span>
            {detail && <Tag color="blue" style={{ fontFamily: font.mono, fontWeight: 600 }}>{detail.sample_no}</Tag>}
          </Space>
        }
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={canWrite && detail ? [
          <Button key="close" onClick={() => setDetail(null)}>닫기</Button>,
          <Button key="edit" type="primary" icon={<EditOutlined />}
            onClick={() => { const d = detail; setDetail(null); if (d) openEdit(d); }}>수정</Button>,
        ] : null}
        width={760}
        destroyOnClose
      >
        {detail && (
          <>
            <Descriptions bordered size="small" column={2} labelStyle={{ width: 120, fontSize: 12 }} contentStyle={{ fontSize: 12 }}>
              <Descriptions.Item label="시험번호">{detail.test_number || '-'}</Descriptions.Item>
              <Descriptions.Item label="시료번호">{detail.sample_no || '-'}</Descriptions.Item>
              <Descriptions.Item label="항목명" span={2}>{detail.title || '-'}</Descriptions.Item>
              <Descriptions.Item label="약제명" span={2}>{detail.pesticide_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="주성분 함량(%)">{detail.active_ingredient || '-'}</Descriptions.Item>
              <Descriptions.Item label="Lot.">{detail.lot_no || '-'}</Descriptions.Item>
              <Descriptions.Item label="인수량">{detail.received_qty || '-'}</Descriptions.Item>
              <Descriptions.Item label="제조년월일">{detail.manufactured_date || '-'}</Descriptions.Item>
              <Descriptions.Item label="약효보증기간">{detail.guarantee_date || '-'}</Descriptions.Item>
              <Descriptions.Item label="의뢰사">{detail.company_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="제조사">{detail.manufacturer || '-'}</Descriptions.Item>
              <Descriptions.Item label="인수일">{detail.received_date || '-'}</Descriptions.Item>
              <Descriptions.Item label="폐기일">{detail.disposal_date || '-'}</Descriptions.Item>
              <Descriptions.Item label="작물폐기유무"><CropDisposalBadge value={detail.crop_disposal} /></Descriptions.Item>
              <Descriptions.Item label="폐기확인서" span={2}>{detail.disposal_confirm || '-'}</Descriptions.Item>
              <Descriptions.Item label="비고" span={2}>{detail.note || '-'}</Descriptions.Item>
            </Descriptions>
            <Divider style={{ margin: '14px 0 8px' }} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {detail.schedule_id
                ? <>연결된 시험일정: <b>{detail.schedule_title || detail.test_number}</b></>
                : '시험일정에 등록되지 않은 시험번호입니다. 시험일정 등록 후 이 물질을 다시 저장하면 자동 연결됩니다.'}
            </Typography.Text>
          </>
        )}
      </Modal>

      {/* ── 선택 n건 일자 일괄등록 — 인수일 / 폐기일 ── */}
      <Modal
        title={
          <Space>
            <CalendarOutlined style={{ color: brand.primary }} />
            <span>일자 일괄등록</span>
            <Tag color={brand.primary} style={{ fontWeight: 600 }}>{selectedIds.length}건</Tag>
          </Space>
        }
        open={bulkDateOpen}
        onCancel={() => setBulkDateOpen(false)}
        onOk={submitBulkDates}
        okText="반영"
        cancelText="취소"
        confirmLoading={bulkDateSaving}
        width={480}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 14 }}>
          선택한 {selectedIds.length}건에 아래 일자를 한 번에 등록합니다.
          비워 둔 항목은 <b>기존 값을 그대로 유지</b>합니다.
        </Typography.Paragraph>
        <Form form={bulkDateForm} layout="vertical">
          <Form.Item name="received_date" label="인수일" style={{ marginBottom: 12 }}>
            <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} placeholder="날짜 선택 (미입력 시 변경 없음)" />
          </Form.Item>
          <Form.Item name="disposal_date" label="폐기일" style={{ marginBottom: 0 }}>
            <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} placeholder="날짜 선택 (미입력 시 변경 없음)" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TestMaterialPage;
